import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Divider,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import API from "../../api/api";

const TICKET_TYPES = [
  { value: "KYC_REVERIFY", label: "KYC Re-verification" },
  { value: "GENERAL", label: "General" },
];

const STATUS_OPTIONS = [
  { value: "open", label: "Open / Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Approved / Resolved" },
  { value: "rejected", label: "Rejected" },
  { value: "closed", label: "Closed" },
];

function StatusChip({ status, type }) {
  const s = String(status || "").toLowerCase();
  const isKyc = String(type || "") === "KYC_REVERIFY";

  if (isKyc) {
    if (s === "resolved") {
      return (
        <Chip
          size="small"
          icon={<CheckCircleOutlineIcon style={{ fontSize: 16, color: "#065f46" }} />}
          label="APPROVED & UPDATED"
          sx={{
            bgcolor: "#ecfdf5",
            color: "#065f46",
            fontWeight: 800,
            border: "1px solid #a7f3d0",
            fontSize: 11,
          }}
        />
      );
    }
    if (s === "rejected") {
      return (
        <Chip
          size="small"
          icon={<CancelOutlinedIcon style={{ fontSize: 16, color: "#991b1b" }} />}
          label="REJECTED"
          sx={{
            bgcolor: "#fef2f2",
            color: "#991b1b",
            fontWeight: 800,
            border: "1px solid #fecaca",
            fontSize: 11,
          }}
        />
      );
    }
    return (
      <Chip
        size="small"
        icon={<HourglassEmptyIcon style={{ fontSize: 16, color: "#92400e" }} />}
        label="PENDING REVIEW"
        sx={{
          bgcolor: "#fffbeb",
          color: "#92400e",
          fontWeight: 800,
          border: "1px solid #fde68a",
          fontSize: 11,
        }}
      />
    );
  }

  const STATUS_COLOR = {
    open: "default",
    in_progress: "info",
    resolved: "success",
    rejected: "error",
    closed: "default",
  };
  const color = STATUS_COLOR[s] || "default";
  return <Chip size="small" label={String(status || "").toUpperCase()} color={color} variant="outlined" />;
}

export default function AdminSupport() {
  // Filters
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  // Data
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  // Selection + details
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Admin reply
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  // Status/assignee updates
  const [busyAction, setBusyAction] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");

  // Rejection modal
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  // Current admin id for "Assign to me"
  const [me, setMe] = useState(null);

  // Load current admin info
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await API.get("/accounts/me/");
        if (mounted) setMe(res?.data || null);
      } catch (_) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  async function fetchTickets() {
    try {
      setLoading(true);
      setErr("");
      setInfo("");
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (search && search.trim().length >= 2) params.search = search.trim();
      const res = await API.get("/admin/support/tickets/", { params, cacheTTL: 0 });
      const arr = Array.isArray(res?.data?.results) ? res.data.results : Array.isArray(res?.data) ? res.data : [];
      setTickets(arr);
      // if a ticket is selected, refresh the detail from the new list
      if (selectedId) {
        const d = arr.find((t) => t.id === selectedId);
        if (d) setDetail(d);
      }
    } catch (e) {
      setErr("Failed to load support tickets.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchTickets();
  }, [statusFilter, typeFilter]);

  function selectTicket(id) {
    setSelectedId(id);
    setDetailLoading(true);
    const d = tickets.find((t) => t.id === id) || null;
    setDetail(d);
    setDetailLoading(false);
  }

  async function sendAdminReply() {
    if (!selectedId) return;
    const text = String(reply || "").trim();
    if (!text) return;
    try {
      setSending(true);
      await API.post(`/admin/support/tickets/${selectedId}/messages/`, { message: text });
      setReply("");
      await fetchTickets();
      setInfo("Reply sent.");
    } catch (e) {
      setErr("Failed to send reply.");
    } finally {
      setSending(false);
    }
  }

  async function assignToMe() {
    if (!selectedId || !me?.id) return;
    try {
      setBusyAction(true);
      const res = await API.patch(`/admin/support/tickets/${selectedId}/`, { admin_assignee: me.id });
      if (res?.data) setDetail(res.data);
      await fetchTickets();
      setInfo("Assigned to you.");
    } catch (e) {
      setErr("Failed to assign ticket.");
    } finally {
      setBusyAction(false);
    }
  }

  async function updateStatus(newStatus) {
    if (!selectedId) return;
    try {
      setBusyAction(true);
      const payload = { status: newStatus };
      if (resolutionNote && resolutionNote.trim()) {
        payload.resolution_note = resolutionNote.trim();
      }
      const res = await API.patch(`/admin/support/tickets/${selectedId}/`, payload);
      setResolutionNote("");
      await fetchTickets();
      setInfo(`Status updated: ${newStatus}`);
      if (["resolved", "rejected", "closed"].includes(newStatus)) {
        setSelectedId(null);
        setDetail(null);
      } else if (res?.data) {
        setDetail(res.data);
      }
    } catch (e) {
      setErr("Failed to update status.");
    } finally {
      setBusyAction(false);
    }
  }

  async function handleKycAction(action = "approve") {
    if (!selectedId) return;
    try {
      setBusyAction(true);
      const payload = { action };
      if (action === "reject") {
        if (rejectReason.trim()) payload.note = rejectReason.trim();
      } else {
        if (resolutionNote.trim()) payload.note = resolutionNote.trim();
      }
      const res = await API.post(`/admin/support/tickets/${selectedId}/approve-kyc/`, payload);
      setResolutionNote("");
      setRejectOpen(false);
      setRejectReason("");
      await fetchTickets();
      setInfo(action === "reject" ? "KYC re-verification request rejected." : "✓ KYC bank profile approved & updated live successfully!");
      setSelectedId(null);
      setDetail(null);
    } catch (e) {
      setErr(action === "reject" ? "Failed to reject KYC request." : "Failed to approve KYC bank update.");
    } finally {
      setBusyAction(false);
    }
  }

  const Filters = () => (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          Support Tickets
        </Typography>
        <Button size="small" variant="outlined" onClick={fetchTickets}>
          Refresh
        </Button>
      </Stack>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="type-filter-label">Type</InputLabel>
            <Select
              labelId="type-filter-label"
              label="Type"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              {TICKET_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            size="small"
            label="Search subject/message"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                fetchTickets();
              }
            }}
          />
        </Grid>
      </Grid>
    </Paper>
  );

  const TicketList = () => (
    <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : tickets.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No tickets found for the selected filters.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {tickets.map((t) => (
            <Box
              key={t.id}
              onClick={() => selectTicket(t.id)}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: selectedId === t.id ? "#0ea5e9" : "#e2e8f0",
                background: selectedId === t.id ? "rgba(14,165,233,0.06)" : "#fff",
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  borderColor: "#38bdf8",
                  background: selectedId === t.id ? "rgba(14,165,233,0.08)" : "#f8fafc",
                },
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1, mb: 0.5 }}>
                <Stack direction="row" alignItems="center" sx={{ gap: 0.75 }}>
                  <Chip
                    size="small"
                    label={t.type === "KYC_REVERIFY" ? "KYC" : String(t.type || "")}
                    color={t.type === "KYC_REVERIFY" ? "primary" : "default"}
                    sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                  />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 13 }}>
                    {t.subject}
                  </Typography>
                </Stack>
                <StatusChip status={t.status} type={t.type} />
              </Stack>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                {t.username ? `${t.username}${t.full_name ? ` (${t.full_name})` : ""}${t.phone ? ` • ${t.phone}` : ""}` : ""}
                {t.pincode ? ` • ${t.pincode}` : ""} {t.state_name ? ` • ${t.state_name}` : ""}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.disabled", display: "block", mt: 0.25 }}>
                Updated: {t.updated_at ? new Date(t.updated_at).toLocaleString() : "-"}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  );

  const TicketDetail = () => {
    if (!selectedId) {
      return (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
          <Typography variant="body1" color="text.secondary">
            Select a ticket from the list to review details and take action.
          </Typography>
        </Paper>
      );
    }
    if (detailLoading) {
      return (
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={28} />
        </Paper>
      );
    }
    if (!detail) {
      return (
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Alert severity="error">Ticket not found.</Alert>
        </Paper>
      );
    }

    const messages = Array.isArray(detail.messages) ? detail.messages : [];
    const isKyc = String(detail.type || "") === "KYC_REVERIFY";
    const oldKyc = detail.user_kyc || {};
    const newKyc = detail.requested_kyc || {};

    return (
      <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Back Button & Status Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1, borderBottom: "1px solid #e2e8f0" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => {
              setSelectedId(null);
              setDetail(null);
            }}
            sx={{
              fontWeight: 800,
              textTransform: "none",
              color: "#0f172a",
              bgcolor: "#f1f5f9",
              borderRadius: 2,
              px: 1.5,
              py: 0.6,
              fontSize: 12.5,
              border: "1px solid #cbd5e1",
              "&:hover": { bgcolor: "#e2e8f0" },
            }}
          >
            ← Back to Tickets
          </Button>
          <StatusChip status={detail.status} type={detail.type} />
        </Stack>

        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
            <Chip
              size="small"
              label={isKyc ? "KYC RE-VERIFICATION" : String(detail.type || "")}
              color={isKyc ? "primary" : "default"}
              sx={{ fontWeight: 700 }}
            />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {detail.subject}
            </Typography>
          </Stack>
        </Stack>

        {/* User Info Bar */}
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "#f8fafc" }}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Consumer: <strong>{detail.full_name || detail.username}</strong> ({detail.username})
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Phone: <strong>{detail.phone || "—"}</strong> • Pincode: <strong>{detail.pincode || "—"}</strong> • State: <strong>{detail.state_name || "—"}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ textAlign: { sm: "right" } }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Assignee: <strong>{detail.admin_assignee_username ? detail.admin_assignee_username : "Unassigned"}</strong>
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Created: {detail.created_at ? new Date(detail.created_at).toLocaleString() : "-"}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* User Request Message */}
        {detail.message ? (
          <Alert severity="info" sx={{ my: 0.5, py: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>User Request / Notes:</Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{detail.message}</Typography>
          </Alert>
        ) : null}

        {/* Resolution Note if present */}
        {detail.resolution_note ? (
          <Alert severity={detail.status === "rejected" ? "error" : "success"} sx={{ my: 0.5, py: 0.75 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
              {detail.status === "rejected" ? "Rejection Note:" : "Approval / Resolution Audit Note:"}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{detail.resolution_note}</Typography>
          </Alert>
        ) : null}

        {/* KYC SIDE-BY-SIDE COMPARISON CARDS */}
        {isKyc && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1, color: "#1e293b", display: "flex", alignItems: "center", gap: 0.75 }}>
              <AccountBalanceIcon fontSize="small" sx={{ color: "#0ea5e9" }} />
              Bank Profile Comparison: Current Live vs Requested Update
            </Typography>

            <Grid container spacing={2}>
              {/* CURRENT LIVE BANK PROFILE */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f8fafc",
                    height: "100%",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#475569" }}>
                      Current Verified Profile (Live)
                    </Typography>
                    {oldKyc?.verified ? (
                      <Chip size="small" label="VERIFIED" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 800 }} />
                    ) : (
                      <Chip size="small" label="UNVERIFIED" color="default" sx={{ height: 20, fontSize: 10 }} />
                    )}
                  </Stack>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Bank Name</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
                        {oldKyc?.bank_name || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Account Number</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, fontFamily: "monospace", color: "#1e293b" }}>
                        {oldKyc?.bank_account_number || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>IFSC Code</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, fontFamily: "monospace", color: "#1e293b" }}>
                        {oldKyc?.ifsc_code || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>Account Holder</Typography>
                      <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#1e293b" }}>
                        {oldKyc?.account_holder || detail.full_name || detail.username || "—"}
                      </Typography>
                    </Box>
                    {oldKyc?.verified_at && (
                      <Typography variant="caption" sx={{ color: "#94a3b8", mt: 0.5 }}>
                        Last Verified: {new Date(oldKyc.verified_at).toLocaleDateString()}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* REQUESTED NEW BANK PROFILE */}
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "#f0fdf4",
                    height: "100%",
                    border: "1.5px solid #86efac",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#166534", display: "flex", alignItems: "center", gap: 0.5 }}>
                      <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#16a34a" }} />
                      Requested New Profile
                    </Typography>
                    <Chip
                      size="small"
                      label={detail.status === "resolved" ? "ACTIVE / APPLIED" : "NEW REQUEST"}
                      sx={{
                        height: 20,
                        fontSize: 10,
                        fontWeight: 800,
                        bgcolor: detail.status === "resolved" ? "#bbf7d0" : "#dcfce7",
                        color: "#15803d",
                      }}
                    />
                  </Stack>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 600 }}>Bank Name</Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#0f172a" }}>
                        {newKyc?.bank_name || oldKyc?.bank_name || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 600 }}>Account Number</Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 13, fontFamily: "monospace", color: "#0f172a" }}>
                        {newKyc?.bank_account_number || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 600 }}>IFSC Code</Typography>
                      <Typography sx={{ fontWeight: 800, fontSize: 13, fontFamily: "monospace", color: "#0f172a" }}>
                        {newKyc?.ifsc_code || "—"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#15803d", fontWeight: 600 }}>Account Holder</Typography>
                      <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                        {newKyc?.account_holder || detail.full_name || detail.username || "—"}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* DEDICATED ACTION BAR */}
        <Divider sx={{ my: 0.5 }} />

        {isKyc ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, p: 1.5, bgcolor: "#f8fafc", borderRadius: 2 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
              FINANCIAL APPROVAL ACTIONS:
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleOutlineIcon />}
                onClick={() => handleKycAction("approve")}
                disabled={busyAction}
                sx={{
                  fontWeight: 800,
                  px: 2.5,
                  py: 0.8,
                  bgcolor: "#059669",
                  "&:hover": { bgcolor: "#047857" },
                }}
              >
                {detail.status === "resolved" ? "Re-Apply & Update Bank Profile" : "Approve & Update Bank Profile"}
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelOutlinedIcon />}
                onClick={() => setRejectOpen(true)}
                disabled={busyAction || detail.status === "rejected"}
                sx={{ fontWeight: 800, px: 2 }}
              >
                Reject Request
              </Button>

              <Box sx={{ flexGrow: 1 }} />

              <Button size="small" variant="outlined" onClick={assignToMe} disabled={busyAction || !me?.id}>
                Assign to me
              </Button>
            </Stack>

            <TextField
              size="small"
              label="Admin resolution / approval audit note (optional)"
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              placeholder="Add any internal compliance note..."
              fullWidth
            />
          </Box>
        ) : (
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
            <Button size="small" variant="outlined" onClick={assignToMe} disabled={busyAction || !me?.id}>
              Assign to me
            </Button>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="status-change-label">Change status</InputLabel>
              <Select
                labelId="status-change-label"
                label="Change status"
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) updateStatus(v);
                }}
              >
                <MenuItem value="">
                  <em>Select…</em>
                </MenuItem>
                {STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Resolution note (optional)"
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
          </Stack>
        )}

        <Divider />

        {/* Conversation Thread */}
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Conversation Thread ({messages.length})
        </Typography>
        <Box sx={{ maxHeight: 300, overflowY: "auto", pr: 1, border: "1px solid #e2e8f0", borderRadius: 1.5, p: 1.5, bgcolor: "#fafafa" }}>
          {messages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No messages yet in this ticket thread.
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {messages.map((m) => (
                <Box
                  key={m.id}
                  sx={{
                    p: 1.25,
                    borderRadius: 1.5,
                    border: "1px solid #e2e8f0",
                    background: "#fff",
                  }}
                >
                  <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: 13 }}>
                      {m.author_username || "User"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "#334155" }}>
                    {m.message}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* Admin reply */}
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            size="small"
            placeholder="Type a message / reply to consumer…"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                sendAdminReply();
              }
            }}
          />
          <Button variant="contained" onClick={sendAdminReply} disabled={sending || !String(reply || "").trim()}>
            {sending ? "Sending..." : "Send"}
          </Button>
        </Stack>
      </Paper>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1, md: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900, color: "#0f172a" }}>
          Admin Support & KYC Desk
        </Typography>
        {info ? <Alert severity="success" sx={{ py: 0.5 }}>{info}</Alert> : null}
      </Stack>
      {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}

      <Grid container spacing={2}>
        {!selectedId ? (
          <>
            <Grid item xs={12}>
              <Filters />
            </Grid>
            <Grid item xs={12}>
              <TicketList />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <TicketDetail />
          </Grid>
        )}
      </Grid>

      {/* Reject Modal */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Reject KYC Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please provide a reason for rejecting this bank detail re-verification request (e.g. Account name mismatch, invalid IFSC, unverified documents).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Rejection Reason"
            multiline
            rows={3}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Bank account holder name does not match user KYC name..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRejectOpen(false)} disabled={busyAction}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleKycAction("reject")}
            disabled={busyAction}
            sx={{ fontWeight: 700 }}
          >
            {busyAction ? "Rejecting..." : "Confirm Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
